"use client";

import { useQuery } from "@tanstack/react-query";
import { clientApi } from "@/lib/api/client";
import { ENDPOINTS } from "@/constants/api/endpoints";
import { queryKeys } from "@/lib/react-query/query-keys";
import { isApiError } from "@/types/api";
import { PdpPrices } from "@/types/pdp-prices";
import { usePdpStayDates } from "@/hooks/usePdpStayDates";

export interface UsePdpPricesParams {
  accommodationId: string;
  checkIn: string;
  checkOut: string;
  capacity: number;
}

/**
 * PDP stay pricing for one accommodation.
 *
 * Fetched on the client so a slow crawl does not block the rest of the page.
 * The desktop sidebar, mobile prices tab, host name, and capacity share one
 * cache entry per (stay window + capacity).
 */
export function usePdpPrices({
  accommodationId,
  checkIn,
  checkOut,
  capacity,
}: UsePdpPricesParams) {
  return useQuery({
    queryKey: queryKeys.accommodations.pdpPrices(
      accommodationId,
      checkIn,
      checkOut,
      capacity,
    ),
    queryFn: async () => {
      const response = await clientApi.post<PdpPrices>(
        ENDPOINTS.ACCOMMODATIONS.PRICES_PDP(accommodationId),
        { checkIn, checkOut, capacity },
        { showErrorToast: false },
      );

      if (isApiError(response)) {
        throw new Error(response.error?.message || "Failed to load prices");
      }

      return response.data;
    },
    enabled: Boolean(accommodationId && checkIn && checkOut && capacity >= 1),
  });
}

/** Cheapest bookable origin for the current stay — host and capacity read this. */
export function usePdpCheapestOffer(accommodationId: string) {
  const { checkIn, checkOut, capacity } = usePdpStayDates();
  const { data, isPending } = usePdpPrices({
    accommodationId,
    checkIn,
    checkOut,
    capacity,
  });

  return {
    isPending,
    offer: data?.supported?.[0],
  };
}
